/**
 * Why an attempt stopped, in words a manager reads. One table so the Work
 * row, the case file's status pill, the pane's "Why it stopped" line and the
 * backend's "Build stopped: …" gate title say the same thing about the same
 * run (the `jobFailureCodes.ts` shape).
 *
 * Anything unmapped reads "stopped" rather than leaking a snake_case token
 * onto the page: the case file carries the raw reason, and a row that prints
 * `sandbox_allocation_already_consumed` has told the reader nothing.
 *
 * `class` is what a surface reasons about, never the words: a reader that
 * compares output words breaks the day the words improve.
 */
export const STOP_REASON_CLASSES = [
    // The machine's fault, never the plan's: running the same plan again is
    // the honest next step.
    "infra",
    // The attempt ran out of time. Deliberately NOT infra: a run that used its
    // whole budget may well have a plan problem.
    "timeout",
    // The build did something the approved plan did not allow.
    "plan",
    // The build itself failed.
    "build",
    // A person ended the attempt from the page.
    "person",
];
function copy(cls, words) {
    return { class: cls, words };
}
/** The stop codes the runtime writes to `runs.stop_reason` / a gate payload. */
export const STOP_REASON_CODES = {
    sandbox_allocation_already_consumed: copy("infra", "sandbox was already used"),
    sandbox_run_not_eligible: copy("infra", "no sandbox could be allocated"),
    work_missions_emergency_stopped: copy("infra", "builds were paused by an operator"),
    // The older code for the same operator stop, on runs before 2026-09-24.
    candidate_execution_disabled: copy("infra", "builds were paused by an operator"),
    terminal_run_candidate_missing: copy("infra", "the build result was lost before review"),
    candidate_registry_missing: copy("infra", "the build result was lost before review"),
    worker_timed_out: copy("timeout", "build timed out"),
    // The Temporal workflow driving the build failed around it.
    run_execution_failed: copy("infra", "the build's workflow stopped unexpectedly"),
    harness_timed_out: copy("timeout", "build timed out"),
    // The host stopped a build whose harness showed no progress past its stall
    // window with no tool running (YAG-875).
    harness_silent: copy("infra", "the build stopped responding"),
    candidate_outside_scope: copy("plan", "changed files outside the approved plan"),
    worker_failed: copy("build", "build failed"),
    // The change was built, and saving it failed on two deliveries in a row.
    candidate_persistence_failed: copy("infra", "the finished change could not be saved"),
    taken_over: copy("person", "taken over by a person"),
    // The delivery's GitHub read token could not be minted. A wait or a GitHub
    // outage is the machine's; an App that no longer covers the repository is
    // a person's to reconnect.
    github_read_token_rate_limited: copy("infra", "GitHub is rate-limiting this repository's App installation"),
    github_read_token_unavailable: copy("infra", "GitHub could not issue a read token for this repository"),
    github_installation_gone: copy("plan", "the GitHub App no longer covers this repository"),
    // Someone closed the build's pull request on GitHub without merging it: the
    // legacy lane's stop ask, and a one-attempt lane stop that opened none.
    pr_closed_unmerged: copy("person", "the pull request was closed on GitHub without merging"),
    // Wright gave up continuing because the ticket's one budget is spent (a
    // stop gate's `payload.gaveUp`): a person gives it more in Team settings.
    ticket_budget: copy("person", "the ticket used its budget"),
    // The workspace's credits could not cover the build's sandbox: a person
    // adds credits, and the build resumes on its own.
    sandbox_credits_exhausted: copy("person", "the workspace ran out of credits"),
};
/**
 * Facts that reach us as free text rather than a code: the reapers' stale
 * markers ("stale: planning worker lost", the older "stale: no heartbeat")
 * and a timeout sentence inside a failure reason.
 */
export const STOP_REASON_FACTS = {
    worker_lost: copy("infra", "worker was lost during a deploy"),
    timed_out: copy("timeout", "build timed out"),
};
const STALE_WORKER = /^stale:.*\b(worker lost|no heartbeat)\b/;
/**
 * The free-text matchers, in order. The wire carries both shapes for the same
 * fact (`worker_timed_out` from the classifier, "the step timed out" from a
 * failure reason), so every matcher reads the normalised text and is written
 * once.
 */
const FREE_TEXT = [
    [
        (r) => r.includes("sandbox allocation already consumed"),
        STOP_REASON_CODES.sandbox_allocation_already_consumed,
    ],
    [(r) => r.includes("candidate outside scope"), STOP_REASON_CODES.candidate_outside_scope],
    [
        (r) => r.includes("candidate registry missing") || r.includes("terminal run candidate missing"),
        STOP_REASON_CODES.candidate_registry_missing,
    ],
    [(r) => r.includes("timeout") || r.includes("timed out"), STOP_REASON_FACTS.timed_out],
    [(r) => STALE_WORKER.test(r), STOP_REASON_FACTS.worker_lost],
    [(r) => r.includes("worker failed"), STOP_REASON_CODES.worker_failed],
    [(r) => r.includes("taken over"), STOP_REASON_CODES.taken_over],
    [(r) => r.includes("sandbox run not eligible"), STOP_REASON_CODES.sandbox_run_not_eligible],
    [
        (r) => r.includes("work missions emergency stopped") || r.includes("candidate execution disabled"),
        STOP_REASON_CODES.work_missions_emergency_stopped,
    ],
];
function isStopReasonCode(value) {
    return Object.prototype.hasOwnProperty.call(STOP_REASON_CODES, value);
}
/** The copy for a stop reason (a code or free text), or null when unknown. */
export function stopReasonCopy(raw) {
    if (!raw)
        return null;
    const exact = raw.trim().toLowerCase();
    if (isStopReasonCode(exact))
        return STOP_REASON_CODES[exact];
    const reason = exact.replace(/[_-]+/g, " ");
    for (const [matches, found] of FREE_TEXT)
        if (matches(reason))
            return found;
    return null;
}
/** "build timed out", or "stopped" when nothing names the reason. */
export function stopReasonWords(raw) {
    return stopReasonCopy(raw)?.words ?? "stopped";
}
/** Which kind of stop this is, or null when the reason is unknown. */
export function stopReasonClass(raw) {
    return stopReasonCopy(raw)?.class ?? null;
}
//# sourceMappingURL=stopReasonWords.js.map